import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { api } from '../services/api';

const ScorecardForm = ({ scorecard, matches, teams, players, onSubmit, onCancel, isLoading }) => {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
    defaultValues: scorecard || {}
  });

  const [step, setStep] = useState(scorecard ? 2 : 1); // 1: Toss Setup, 2: Live Scoring
  const [currentInnings, setCurrentInnings] = useState(1); // 1 or 2
  const [currentOver, setCurrentOver] = useState(0);
  const [currentBall, setCurrentBall] = useState(0);
  const [striker, setStriker] = useState('');
  const [nonStriker, setNonStriker] = useState('');
  const [bowler, setBowler] = useState('');
  const [ballHistory, setBallHistory] = useState([]);
  const [team1Score, setTeam1Score] = useState({ runs: 0, wickets: 0, overs: 0, runRate: 0 });
  const [team2Score, setTeam2Score] = useState({ runs: 0, wickets: 0, overs: 0, runRate: 0 });
  const [tossData, setTossData] = useState(null);
  const [scorecardId, setScorecardId] = useState(scorecard?.id || null);
  const [matchPlayers, setMatchPlayers] = useState({ team1_players: [], team2_players: [] });
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [selectedBallType, setSelectedBallType] = useState(null);
  const [extraRuns, setExtraRuns] = useState(0);
  const [batterRuns, setBatterRuns] = useState(0);
  const [playerStats, setPlayerStats] = useState({ batting: [], bowling: [] });
  const [innings1Stats, setInnings1Stats] = useState({ batting: [], bowling: [] });
  const [innings2Stats, setInnings2Stats] = useState({ batting: [], bowling: [] });
  // Dismissal state
  const [showDismissalModal, setShowDismissalModal] = useState(false);
  const [dismissalType, setDismissalType] = useState('');
  const [dismissedBatter, setDismissedBatter] = useState('striker');
  const [fielder, setFielder] = useState('');
  // Extras breakdown
  const [extras, setExtras] = useState({ wides: 0, no_balls: 0, byes: 0, leg_byes: 0, total: 0 });
  // Partnership
  const [partnership, setPartnership] = useState({ runs: 0, balls: 0 });
  // Fall of wickets
  const [fallOfWickets, setFallOfWickets] = useState([]);
  // Advanced features state
  const [matchFormat, setMatchFormat] = useState('T20');
  const [maxOversPerBowler, setMaxOversPerBowler] = useState(4);
  const [totalOvers, setTotalOvers] = useState(20);
  const [bowlerInfo, setBowlerInfo] = useState(null);
  const [powerplayInfo, setPowerplayInfo] = useState(null);
  const [inningsStatus, setInningsStatus] = useState({ completed: false, reason: null });
  const [showInningsCompleteModal, setShowInningsCompleteModal] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [requiredRunRate, setRequiredRunRate] = useState(null);
  const [target, setTarget] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [maidenOver, setMaidenOver] = useState(null);
  // Super over state
  // eslint-disable-next-line no-unused-vars
  const [showSuperOver, setShowSuperOver] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [superOverData, setSuperOverData] = useState(null);

  // Dismissal types
  const dismissalTypes = [
    { value: 'bowled', label: 'Bowled', needsFielder: false, creditsBowler: true },
    { value: 'caught', label: 'Caught', needsFielder: true, creditsBowler: true },
    { value: 'lbw', label: 'LBW', needsFielder: false, creditsBowler: true },
    { value: 'run_out', label: 'Run Out', needsFielder: true, creditsBowler: false },
    { value: 'stumped', label: 'Stumped', needsFielder: true, creditsBowler: true },
    { value: 'hit_wicket', label: 'Hit Wicket', needsFielder: false, creditsBowler: true },
    { value: 'obstructing_field', label: 'Obstructing Field', needsFielder: false, creditsBowler: false },
    { value: 'retired_out', label: 'Retired Out', needsFielder: false, creditsBowler: false },
    { value: 'retired_hurt', label: 'Retired Hurt', needsFielder: false, creditsBowler: false },
  ];

  const selectedMatch = watch('match_id');
  const match = matches.find(m => m.id === selectedMatch);

  // Load saved scorecard data when editing
  useEffect(() => {
    if (scorecard) {
      // Set toss data
      setTossData({
        toss_winner: scorecard.toss_winner,
        toss_decision: scorecard.toss_decision
      });

      // Load match format and overs settings
      if (scorecard.match_format) {
        setMatchFormat(scorecard.match_format);
      }
      if (scorecard.total_overs !== undefined && scorecard.total_overs !== null) {
        setTotalOvers(scorecard.total_overs);
      }
      if (scorecard.max_overs_per_bowler !== undefined && scorecard.max_overs_per_bowler !== null) {
        setMaxOversPerBowler(scorecard.max_overs_per_bowler);
      }

      console.log('Loaded scorecard total_overs:', scorecard.total_overs);

      // Load team scores
      setTeam1Score({
        runs: scorecard.team1_innings?.total_runs || 0,
        wickets: scorecard.team1_innings?.total_wickets || 0,
        overs: scorecard.team1_innings?.total_overs || 0
      });

      setTeam2Score({
        runs: scorecard.team2_innings?.total_runs || 0,
        wickets: scorecard.team2_innings?.total_wickets || 0,
        overs: scorecard.team2_innings?.total_overs || 0
      });

      // Load ball history
      const allBalls = [
        ...(scorecard.team1_innings?.ball_by_ball || []),
        ...(scorecard.team2_innings?.ball_by_ball || [])
      ].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

      setBallHistory(allBalls.map(ball => ({
        id: ball.timestamp,
        over: ball.over,
        ball: ball.ball,
        type: ball.type,
        runs: ball.runs,
        striker: ball.striker,
        bowler: ball.bowler,
        timestamp: ball.timestamp
      })));

      // Calculate current over and ball from current innings
      // Determine which innings we're on based on team1 innings completion
      const isSecondInnings = scorecard.team1_innings?.is_innings_completed || false;
      setCurrentInnings(isSecondInnings ? 2 : 1);

      const currentInningsBalls = isSecondInnings
        ? (scorecard.team2_innings?.ball_by_ball || [])
        : (scorecard.team1_innings?.ball_by_ball || []);

      // Get over/ball from the current innings' total_balls
      const totalBalls = isSecondInnings
        ? (scorecard.team2_innings?.total_balls || 0)
        : (scorecard.team1_innings?.total_balls || 0);

      const calculatedOver = Math.floor(totalBalls / 6);
      const calculatedBall = totalBalls % 6;

      setCurrentOver(calculatedOver);
      setCurrentBall(calculatedBall);

      const lastBall = currentInningsBalls[currentInningsBalls.length - 1];
      if (lastBall) {
        
        // Load striker and bowler from the last ball
        setStriker(lastBall.striker || '');
        setBowler(lastBall.bowler || '');
        
        // For non-striker, we need to determine who was the other batsman
        // Look for the most recent batsman who was on strike before the current striker
        if (lastBall.striker) {
          // Get all unique batsmen from the current innings ball history
          const allBatsmen = [...new Set(currentInningsBalls.map(ball => ball.striker).filter(Boolean))];

          // Find the non-striker by looking at the previous ball's striker
          // or by finding another batsman who was on strike recently
          let nonStriker = '';

          // First, try to find from the previous ball
          if (currentInningsBalls.length > 1) {
            const previousBall = currentInningsBalls[currentInningsBalls.length - 2];
            if (previousBall.striker && previousBall.striker !== lastBall.striker) {
              nonStriker = previousBall.striker;
            }
          }

          // If we still don't have a non-striker, find any other batsman
          if (!nonStriker) {
            nonStriker = allBatsmen.find(batsman => batsman !== lastBall.striker) || '';
          }
          
          setNonStriker(nonStriker);
        }
      }

      // Set default striker and non-striker if none found
      if (!lastBall?.striker && matchPlayers.team1_players.length > 0) {
        // Set first two players as default striker and non-striker
        setStriker(matchPlayers.team1_players[0].id);
        if (matchPlayers.team1_players.length > 1) {
          setNonStriker(matchPlayers.team1_players[1].id);
        }
      }

      // Load player stats for both innings
      const innings1 = {
        batting: scorecard.team1_innings?.batting || [],
        bowling: scorecard.team1_innings?.bowling || []
      };
      const innings2 = {
        batting: scorecard.team2_innings?.batting || [],
        bowling: scorecard.team2_innings?.bowling || []
      };
      setInnings1Stats(innings1);
      setInnings2Stats(innings2);
      // Set current playerStats based on which innings is active
      setPlayerStats(isSecondInnings ? innings2 : innings1);

      // Load man of the match if saved
      if (scorecard.man_of_the_match) {
        setValue('man_of_the_match', scorecard.man_of_the_match);
      }

      // Load match result if completed
      if (scorecard.match_result) {
        setMatchResult(scorecard.match_result);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scorecard, setValue]);

  // Clear striker/non-striker if they become dismissed
  useEffect(() => {
    if (striker) {
      const strikerBatting = playerStats.batting.find(b => b.player_id === striker);
      if (strikerBatting?.is_out) {
        setStriker('');
      }
    }
    if (nonStriker) {
      const nonStrikerBatting = playerStats.batting.find(b => b.player_id === nonStriker);
      if (nonStrikerBatting?.is_out) {
        setNonStriker('');
      }
    }
    // Also ensure striker and non-striker are not the same
    if (striker && nonStriker && striker === nonStriker) {
      setNonStriker('');
    }
  }, [playerStats.batting, striker, nonStriker]);

  // Fetch players for the selected match
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fetchMatchPlayers = async () => {
      if (!selectedMatch) {
        setMatchPlayers({ team1_players: [], team2_players: [] });
        return;
      }

      setLoadingPlayers(true);
      try {
        const playersData = await api.getMatchPlayers(selectedMatch);
        setMatchPlayers(playersData);
        console.log('Debug - Match Players from API:', playersData);
      } catch (error) {
        console.error('Error fetching match players:', error);
        toast.error('Failed to load players for this match');
        setMatchPlayers({ team1_players: [], team2_players: [] });
      } finally {
        setLoadingPlayers(false);
      }
    };

    fetchMatchPlayers();
  }, [selectedMatch]);

  const team1Players = matchPlayers.team1_players || [];
  const team2Players = matchPlayers.team2_players || [];

  const ballTypes = [
    { value: 'dot', label: 'Dot (0)', category: 'runs' },
    { value: '1', label: '1 Run', category: 'runs' },
    { value: '2', label: '2 Runs', category: 'runs' },
    { value: '3', label: '3 Runs', category: 'runs' },
    { value: '4', label: 'FOUR', category: 'boundary' },
    { value: '6', label: 'SIX', category: 'boundary' },
    { value: 'wide', label: 'Wide', category: 'extras' },
    { value: 'no_ball', label: 'No Ball', category: 'extras' },
    { value: 'bye', label: 'Bye', category: 'extras' },
    { value: 'leg_bye', label: 'Leg Bye', category: 'extras' },
    { value: 'wicket', label: 'WICKET', category: 'wicket' }
  ];

  // Helper functions
  const getTeamName = (id) => teams.find(t => t.id === id)?.name || 'Unknown Team';
  const getPlayerName = (id) => players.find(p => p.id === id)?.name || 'Unknown Player';

  const handleTossSubmit = async (data) => {
    try {
      // Call the new initialize scorecard API
      const response = await api.initializeScorecard({
        match_id: data.match_id,
        toss_winner: data.toss_winner,
        toss_decision: data.toss_decision,
        team1_id: match?.team1_id,
        team2_id: match?.team2_id
      });

      if (response.status === 'created' || response.status === 'existing') {
        setScorecardId(response.scorecard_id);
        setTossData({
          toss_winner: data.toss_winner,
          toss_decision: data.toss_decision
        });

        // Set format-specific info from response
        if (response.match_format) {
          setMatchFormat(response.match_format);
        }
        if (response.total_overs) {
          setTotalOvers(response.total_overs);
        }
        if (response.max_overs_per_bowler) {
          setMaxOversPerBowler(response.max_overs_per_bowler);
        }

        // Determine which team bats first based on toss decision
        const battingFirstTeam = data.toss_decision === 'bat' ? data.toss_winner :
          (data.toss_winner === match?.team1_id ? match?.team2_id : match?.team1_id);

        // Set initial batting team
        if (battingFirstTeam === match?.team1_id) {
          setCurrentInnings(1);
        } else {
          setCurrentInnings(2);
        }

        toast.success(`${response.match_format} match initialized! Max ${response.max_overs_per_bowler} overs per bowler.`);
        setStep(2);
      } else {
        toast.error('Failed to initialize scorecard');
      }
    } catch (error) {
      console.error('Error initializing scorecard:', error);
      toast.error('Failed to save toss information');
    }
  };

  const handleBallSubmit = async (ballData) => {
    if (!scorecardId) {
      toast.error('Scorecard not initialized');
      return;
    }

    // Validation: Check if striker is selected
    if (!striker) {
      toast.error('Please select a striker before scoring');
      return;
    }

    // Validation: Check if bowler is selected
    if (!bowler) {
      toast.error('Please select a bowler before scoring');
      return;
    }

    // Validation: Check if non-striker is selected
    if (!nonStriker) {
      toast.error('Please select a non-striker before scoring');
      return;
    }

    // Determine if this is a legal delivery
    const isLegalDelivery = !['wide', 'no_ball'].includes(ballData.type);

    // Calculate runs correctly based on ball type
    let batterRunsToSend = 0;
    let extraRunsToSend = 0;
    let totalRuns = 0;

    if (ballData.type === 'wide') {
      // Wide: 1 run penalty + any additional runs (all to extras)
      extraRunsToSend = ballData.extraRuns || 0;
      totalRuns = 1 + extraRunsToSend;
    } else if (ballData.type === 'no_ball') {
      // No ball: 1 run penalty to extras + batter runs to batter
      extraRunsToSend = ballData.extraRuns || 0;
      batterRunsToSend = ballData.batterRuns || 0;
      totalRuns = 1 + extraRunsToSend + batterRunsToSend;
    } else if (ballData.type === 'bye' || ballData.type === 'leg_bye') {
      // Byes/Leg byes: all runs to extras only
      extraRunsToSend = ballData.runs || ballData.extraRuns || 0;
      totalRuns = extraRunsToSend;
    } else if (ballData.type === 'wicket') {
      // Wicket: any runs before wicket go to batter
      batterRunsToSend = ballData.batterRuns || 0;
      totalRuns = batterRunsToSend;
    } else {
      // Normal runs: all to batter
      batterRunsToSend = ballData.runs || (ballData.type === 'dot' ? 0 : parseInt(ballData.type)) || 0;
      totalRuns = batterRunsToSend;
    }

    try {
      const ballInput = {
        over: currentOver,
        ball: isLegalDelivery ? currentBall + 1 : currentBall,
        type: ballData.type,
        runs: totalRuns,
        batter_runs: batterRunsToSend,
        extra_runs: extraRunsToSend,
        striker: striker,
        non_striker: nonStriker,
        bowler: bowler,
        is_wicket: ballData.isWicket || false,
        dismissal_type: ballData.dismissalType || null,
        dismissed_batter: ballData.dismissedBatter === 'non_striker' ? nonStriker : striker,
        fielder: ballData.fielder || null
      };

      // Call the live score update API
      const response = await api.liveScoreUpdate(scorecardId, {
        match_id: selectedMatch,
        current_innings: currentInnings,
        ball_data: ballInput
      });

      if (response.message === 'Live score updated successfully') {
        // Update local state
        const newBall = {
          id: Date.now(),
          over: currentOver,
          ball: isLegalDelivery ? currentBall + 1 : currentBall,
          type: ballData.type,
          runs: totalRuns,
          batterRuns: batterRunsToSend,
          extraRuns: extraRunsToSend,
          striker: striker,
          bowler: bowler,
          isWicket: ballData.isWicket,
          timestamp: new Date().toISOString()
        };

        setBallHistory([...ballHistory, newBall]);

        // Update scores from API response
        setTeam1Score({
          runs: response.current_score.team1.runs,
          wickets: response.current_score.team1.wickets,
          overs: response.current_score.team1.overs,
          runRate: response.current_score.team1.run_rate || 0
        });
        setTeam2Score({
          runs: response.current_score.team2.runs,
          wickets: response.current_score.team2.wickets,
          overs: response.current_score.team2.overs,
          runRate: response.current_score.team2.run_rate || 0
        });

        // Update extras breakdown
        if (response.extras) {
          setExtras(response.extras);
        }

        // Update partnership
        if (response.partnership) {
          setPartnership(response.partnership);
        }

        // Update player statistics for current innings
        if (response.player_stats) {
          setPlayerStats(response.player_stats);
          // Also update the specific innings stats
          if (currentInnings === 1) {
            setInnings1Stats(response.player_stats);
          } else {
            setInnings2Stats(response.player_stats);
          }
        }

        // Update fall of wickets
        if (response.fall_of_wickets) {
          setFallOfWickets(response.fall_of_wickets);
        }

        // Update advanced features from response
        if (response.bowler_info) {
          setBowlerInfo(response.bowler_info);
        }
        if (response.powerplay) {
          setPowerplayInfo(response.powerplay);
        }
        if (response.innings_status) {
          setInningsStatus(response.innings_status);
          // Show modal when first innings is completed
          if (response.innings_status.completed && currentInnings === 1) {
            setShowInningsCompleteModal(true);
          }
        }
        if (response.required_run_rate) {
          setRequiredRunRate(response.required_run_rate);
        }
        if (response.target) {
          setTarget(response.target);
        }
        if (response.maiden_over) {
          setMaidenOver(response.maiden_over);
          if (response.maiden_over.is_maiden) {
            toast.success('MAIDEN OVER!');
          }
        }
        if (response.match_result) {
          setMatchResult(response.match_result);
          if (response.match_result.result_type === 'tie') {
            toast('Match Tied! Super Over required.');
            setShowSuperOver(true);
          } else {
            toast.success(response.match_result.result_description);
          }
        }

        // Check if bowler limit exceeded warning
        if (response.error === 'bowler_limit_exceeded') {
          toast.error(`Bowler has completed ${response.bowler_overs}/${response.max_overs} overs. Please select another bowler.`);
          return;
        }

        // Update ball/over counters only for legal deliveries
        if (isLegalDelivery) {
          if (currentBall === 5) {
            setCurrentOver(prev => prev + 1);
            setCurrentBall(0);
            // Swap strikers at end of over
            setStriker(nonStriker);
            setNonStriker(striker);
            // Change bowler after each over
            setBowler('');
            toast('Over completed! Please select a new bowler.');
          } else {
            setCurrentBall(prev => prev + 1);
          }
        }

        // Handle wicket - reset striker or non-striker for next ball
        if (ballData.isWicket) {
          if (ballData.dismissedBatter === 'non_striker') {
            setNonStriker('');
            toast('Wicket! Please select a new non-striker.');
          } else {
            setStriker('');
            toast('Wicket! Please select a new batsman.');
          }
        }

        // Handle striker rotation for odd runs
        let shouldChangeStrike = false;

        if (ballData.type === 'wide') {
          // For wide: change strike only if extra runs taken are odd
          shouldChangeStrike = (ballData.extraRuns || 0) % 2 !== 0;
        } else if (ballData.type === 'no_ball') {
          // For no ball: change strike based on total runs (batter + extra)
          const totalNoBallRuns = (ballData.batterRuns || 0) + (ballData.extraRuns || 0);
          shouldChangeStrike = totalNoBallRuns % 2 !== 0;
        } else if (ballData.type === 'bye' || ballData.type === 'leg_bye') {
          // For byes/leg byes: change strike if runs are odd
          shouldChangeStrike = (ballData.extraRuns || ballData.runs || 0) % 2 !== 0;
        } else if (isLegalDelivery && !ballData.isWicket) {
          // For legal deliveries: change strike on odd runs
          shouldChangeStrike = batterRunsToSend % 2 !== 0;
        }

        // Don't change strike if it's end of over (already handled above)
        if (shouldChangeStrike && !(isLegalDelivery && currentBall === 5)) {
          const tempStriker = striker;
          setStriker(nonStriker);
          setNonStriker(tempStriker);
        }

        toast.success('Ball scored!');
      }
    } catch (error) {
      console.error('Error adding ball:', error);
      console.error('Error details:', error.message, error.response?.data);
      toast.error(`Failed to score ball: ${error.message || 'Unknown error'}`);
    }
  };

  const handleCompleteScorecard = async (data) => {
    if (!scorecardId) {
      toast.error('Scorecard not initialized');
      return;
    }

    try {
      const response = await api.completeScorecard(scorecardId, data.man_of_the_match);
      
      if (response.id) {
        toast.success('Scorecard completed successfully!');
        // Don't call onSubmit - the scorecard is already completed via API
        // Just close the form
        onCancel();
      }
    } catch (error) {
      console.error('Error completing scorecard:', error);
      toast.error('Failed to complete scorecard');
    }
  };

  const handleBallTypeSelect = (ballType) => {
    if (ballType === 'wicket') {
      setSelectedBallType(ballType);
      setShowDismissalModal(true);
    } else {
      setSelectedBallType(ballType);
      setExtraRuns(0);
      setBatterRuns(0);
    }
  };

  const handleExtraRunsSelect = (runs) => {
    setExtraRuns(runs);
  };

  const handleBatterRunsSelect = (runs) => {
    setBatterRuns(runs);
  };

  const confirmBallScoring = () => {
    if (!selectedBallType) {
      toast.error('Please select a ball type');
      return;
    }

    const ballData = {
      type: selectedBallType,
      runs: ['1', '2', '3', '4', '6'].includes(selectedBallType) ? parseInt(selectedBallType) : 0,
      batterRuns: batterRuns,
      extraRuns: extraRuns,
      isWicket: false
    };

    handleBallSubmit(ballData);

    // Reset selection
    setSelectedBallType(null);
    setExtraRuns(0);
    setBatterRuns(0);
  };

  const confirmWicket = () => {
    if (!dismissalType) {
      toast.error('Please select a dismissal type');
      return;
    }

    const selectedDismissal = dismissalTypes.find(d => d.value === dismissalType);
    if (selectedDismissal?.needsFielder && !fielder) {
      toast.error('Please select the fielder');
      return;
    }

    const ballData = {
      type: 'wicket',
      runs: 0,
      batterRuns: batterRuns,
      extraRuns: 0,
      isWicket: true,
      dismissalType: dismissalType,
      dismissedBatter: dismissedBatter,
      fielder: fielder || null
    };

    handleBallSubmit(ballData);

    // Reset
    setShowDismissalModal(false);
    setSelectedBallType(null);
    setDismissalType('');
    setDismissedBatter('striker');
    setFielder('');
    setBatterRuns(0);
  };

  const handleUndoBall = async () => {
    if (!scorecardId) {
      toast.error('Scorecard not initialized');
      return;
    }

    if (ballHistory.length === 0) {
      toast.error('No balls to undo');
      return;
    }

    try {
      const response = await api.undoBall(scorecardId, currentInnings, selectedMatch);

      if (response.message === 'Last ball undone successfully') {
        // Remove last ball from history
        setBallHistory(prev => prev.slice(0, -1));

        // Update scores
        setTeam1Score({
          runs: response.current_score.team1.runs,
          wickets: response.current_score.team1.wickets,
          overs: response.current_score.team1.overs,
          runRate: team1Score.runRate
        });
        setTeam2Score({
          runs: response.current_score.team2.runs,
          wickets: response.current_score.team2.wickets,
          overs: response.current_score.team2.overs,
          runRate: team2Score.runRate
        });

        // Recalculate current over and ball
        const lastBall = response.undone_ball;
        if (lastBall) {
          // If the undone ball was legal, decrement ball count
          if (!['wide', 'no_ball'].includes(lastBall.type)) {
            if (currentBall === 0 && currentOver > 0) {
              setCurrentOver(prev => prev - 1);
              setCurrentBall(5);
            } else if (currentBall > 0) {
              setCurrentBall(prev => prev - 1);
            }
          }
        }

        toast.success('Last ball undone!');
      }
    } catch (error) {
      console.error('Error undoing ball:', error);
      toast.error('Failed to undo ball');
    }
  };

  const resetInnings = async () => {
    if (!scorecardId) {
      toast.error('Scorecard not initialized');
      return;
    }

    try {
      // Call API to reset innings in backend
      const response = await api.resetInnings(scorecardId, currentInnings);
      
      // Update frontend state
      setCurrentOver(0);
      setCurrentBall(0);
      setBallHistory([]);
      
      // Update scores from API response
      setTeam1Score({
        runs: response.current_score.team1.runs,
        wickets: response.current_score.team1.wickets,
        overs: 0
      });
      setTeam2Score({
        runs: response.current_score.team2.runs,
        wickets: response.current_score.team2.wickets,
        overs: 0
      });
      
      setStriker('');
      setNonStriker('');
      setBowler('');
      
      toast.success(`Innings ${currentInnings} reset successfully!`);
    } catch (error) {
      console.error('Error resetting innings:', error);
      toast.error('Failed to reset innings');
    }
  };

  const switchInnings = () => {
    const newInnings = currentInnings === 1 ? 2 : 1;
    setCurrentInnings(newInnings);
    setPlayerStats(newInnings === 1 ? innings1Stats : innings2Stats);
    setCurrentOver(0);
    setCurrentBall(0);
    setBallHistory([]);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-start mb-4 sm:mb-6 gap-2">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                {scorecard ? 'Edit Scorecard' : 'Create Scorecard'}
              </h2>
              <div className="flex flex-wrap items-center gap-2 sm:space-x-4 mt-2">
                {!scorecard && (
                  <>
                    <div className={`flex items-center space-x-2 ${step === 1 ? 'text-cricket-green' : 'text-gray-400'}`}>
                      <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
                        step === 1 ? 'bg-cricket-green text-white' : 'bg-gray-200 text-gray-600'
                      }`}>
                        1
                      </div>
                      <span className="text-xs sm:text-sm font-medium">Toss Setup</span>
                    </div>
                    <div className="w-4 sm:w-8 h-0.5 bg-gray-300"></div>
                  </>
                )}
                <div className={`flex items-center space-x-1 sm:space-x-2 ${step === 2 ? 'text-cricket-green' : 'text-gray-400'}`}>
                  <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium ${
                    step === 2 ? 'bg-cricket-green text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {scorecard ? '1' : '2'}
                  </div>
                  <span className="text-xs sm:text-sm font-medium">
                    {scorecard ? 'Live Scoring' : 'Live Scoring'}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {step === 1 ? (
            <form onSubmit={handleSubmit(handleTossSubmit)} className="space-y-6">
              {/* Step 1: Toss Setup */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800">Step 1: Toss Setup</h3>
                <p className="text-gray-600">Select match and toss details</p>
              </div>

              {/* Match Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Match *
                </label>
                <select
                  {...register('match_id', { required: 'Match is required' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
                >
                  <option value="">Select a match</option>
                  {matches.map(match => (
                    <option key={match.id} value={match.id}>
                      {getTeamName(match.team1_id)} vs {getTeamName(match.team2_id)} - {new Date(match.scheduled_date).toLocaleDateString()}
                    </option>
                  ))}
                </select>
                {errors.match_id && (
                  <p className="text-red-500 text-sm mt-1">{errors.match_id.message}</p>
                )}
              </div>

              {/* Toss Information Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Toss Winner *
                  </label>
                  <select
                    {...register('toss_winner', { required: 'Toss winner is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
                  >
                    <option value="">Select toss winner</option>
                    {selectedMatch && (
                      <>
                            <option value={match?.team1_id}>
                              {getTeamName(match?.team1_id)}
                            </option>
                            <option value={match?.team2_id}>
                              {getTeamName(match?.team2_id)}
                            </option>
                      </>
                    )}
                  </select>
                  {errors.toss_winner && (
                    <p className="text-red-500 text-sm mt-1">{errors.toss_winner.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Toss Decision *
                  </label>
                  <select
                    {...register('toss_decision', { required: 'Toss decision is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
                  >
                    <option value="">Select decision</option>
                    <option value="bat">Batting</option>
                    <option value="bowl">Bowling</option>
                  </select>
                  {errors.toss_decision && (
                    <p className="text-red-500 text-sm mt-1">{errors.toss_decision.message}</p>
                  )}
                </div>
              </div>

              {/* Step 1 Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t">
                <button
                  type="button"
                  onClick={onCancel}
                  className="px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-6 py-2 bg-cricket-green text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  Next: Start Live Scoring
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit(handleCompleteScorecard)} className="space-y-6">
              {/* Step 2: Live Scoring */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800">
                  {scorecard ? 'Live Scoring' : 'Step 2: Live Scoring'}
                </h3>
                <p className="text-gray-600">
                  {scorecard ? 'Continue scoring or edit existing data' : 'Score ball by ball'}
                </p>
              </div>

              {selectedMatch && (
                <div className="space-y-6">
                  {/* Match Information Display */}
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Match Information</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 text-sm">
                      <div>
                        <span className="text-sm text-gray-600">Match: </span>
                        <span className="font-medium">#{match?.match_number} - {match?.format}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Teams: </span>
                        <span className="font-medium">
                          {match?.team1_name || getTeamName(match?.team1_id)} vs {match?.team2_name || getTeamName(match?.team2_id)}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Venue: </span>
                        <span className="font-medium">{match?.venue}</span>
                      </div>
                    </div>
                  </div>

                  {/* Toss Information Display */}
                  {tossData && (
                    <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
                      <h3 className="text-base sm:text-lg font-semibold text-blue-800 mb-2">Toss Information</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 text-sm">
                        <div>
                          <span className="text-sm text-gray-600">Toss Winner: </span>
                              <span className="font-medium">
                                {getTeamName(tossData.toss_winner)}
                              </span>
                        </div>
                        <div>
                          <span className="text-sm text-gray-600">Decision: </span>
                          <span className="font-medium capitalize">
                            {tossData.toss_decision === 'bat' ? 'Batting' : 'Bowling'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Batting Team Display */}
                  <div className="bg-cricket-green bg-opacity-10 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-cricket-green mb-2">
                        Currently Batting: {currentInnings === 1 ?
                        getTeamName(match?.team1_id) :
                        getTeamName(match?.team2_id)}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {tossData?.toss_decision === 'bat' ? 
                        'Toss winner chose to bat first' : 
                        'Toss winner chose to bowl first'}
                    </p>
                  </div>

                  {/* Innings Selector */}
                  <div className="flex flex-wrap gap-2 sm:gap-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentInnings(1);
                        setPlayerStats(innings1Stats);
                      }}
                      className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg font-medium text-sm ${
                        currentInnings === 1
                          ? 'bg-cricket-green text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      1st Innings
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setCurrentInnings(2);
                        setPlayerStats(innings2Stats);
                      }}
                      className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg font-medium text-sm ${
                        currentInnings === 2
                          ? 'bg-cricket-green text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      2nd Innings
                    </button>
                    <button
                      type="button"
                      onClick={switchInnings}
                      className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
                    >
                      Switch Innings
                    </button>
                  </div>

                  {/* Match Format Badge */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {matchFormat}
                      </span>
                      <span className="text-sm text-gray-600">
                        {totalOvers < 999 ? `${totalOvers} overs` : 'Unlimited'}
                      </span>
                      <span className="text-sm text-gray-600">
                        Max {maxOversPerBowler < 999 ? maxOversPerBowler : '∞'} overs/bowler
                      </span>
                    </div>
                    {powerplayInfo && powerplayInfo.is_active && (
                      <div className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium animate-pulse">
                        POWERPLAY {powerplayInfo.phase} (Overs {powerplayInfo.overs})
                      </div>
                    )}
                  </div>

                  {/* Current Score Display */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        {currentInnings === 1 ? '1st Innings' : '2nd Innings'} - {currentInnings === 1 ? team1Score.overs : team2Score.overs} overs
                      </h3>
                      <div className="text-2xl font-bold text-cricket-green">
                        {currentInnings === 1 ? team1Score.runs : team2Score.runs} / {currentInnings === 1 ? team1Score.wickets : team2Score.wickets}
                      </div>
                      <div className="text-sm text-gray-600">
                        Current: {currentOver}.{currentBall}
                        {' | '}
                        RR: {currentInnings === 1 ? team1Score.runRate : team2Score.runRate}
                      </div>
                      {/* Target and Required Run Rate for 2nd innings */}
                      {currentInnings === 2 && target && (
                        <div className="mt-2 p-2 bg-orange-50 rounded-lg">
                          <div className="text-sm text-orange-800">
                            <strong>Target: {target}</strong> | Need {target - team2Score.runs} runs from {(() => {
                              // Convert cricket overs notation (e.g., 1.3 = 1 over 3 balls) to total balls
                              const overs = team2Score.overs || 0;
                              const completedOvers = Math.floor(overs);
                              const ballsInOver = Math.round((overs - completedOvers) * 10);
                              const totalBallsBowled = (completedOvers * 6) + ballsInOver;
                              return (totalOvers * 6) - totalBallsBowled;
                            })()} balls
                          </div>
                          {requiredRunRate && (
                            <div className="text-sm text-orange-600">
                              Required RR: {requiredRunRate}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Match Result Display */}
                  {matchResult && (
                    <div className={`p-4 rounded-lg text-center ${
                      matchResult.result_type === 'tie' ? 'bg-yellow-100 border border-yellow-300' : 'bg-green-100 border border-green-300'
                    }`}>
                      <h3 className="text-xl font-bold">
                        {matchResult.result_type === 'tie' ? 'MATCH TIED!' : 'MATCH RESULT'}
                      </h3>
                      <p className="text-lg font-semibold">
                        {matchResult.winner_id && getTeamName(matchResult.winner_id)} {matchResult.result_description}
                      </p>
                      {matchResult.result_type === 'tie' && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await api.startSuperOver(scorecardId);
                              setShowSuperOver(true);
                              toast.success('Super Over started!');
                            } catch (error) {
                              toast.error('Failed to start Super Over');
                            }
                          }}
                          className="mt-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
                        >
                          Start Super Over
                        </button>
                      )}
                    </div>
                  )}

                  {/* Bowler Overs Warning */}
                  {bowlerInfo && !bowlerInfo.allowed && (
                    <div className="p-3 bg-red-100 border border-red-300 rounded-lg">
                      <p className="text-red-800 font-medium">
                        Warning: Current bowler has completed {bowlerInfo.overs_bowled}/{bowlerInfo.max_overs} overs. Please select another bowler!
                      </p>
                    </div>
                  )}

                  {/* Player Selection */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Striker *
                        {!striker && (
                          <span className="ml-2 text-red-600 text-sm font-medium">
                            (Required)
                          </span>
                        )}
                      </label>
                      <select
                        value={striker}
                        onChange={(e) => setStriker(e.target.value)}
                        disabled={loadingPlayers}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent disabled:bg-gray-100 ${
                          !striker
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300'
                        }`}
                      >
                        <option value="">
                          {loadingPlayers ? 'Loading players...' : 'Select striker'}
                        </option>
                        {(currentInnings === 1 ? team1Players : team2Players)
                          .filter(player => {
                            // Don't show non-striker in striker dropdown
                            if (player.id === nonStriker) return false;
                            // Don't show dismissed batters
                            const battingEntry = playerStats.batting.find(b => b.player_id === player.id);
                            if (battingEntry?.is_out) return false;
                            return true;
                          })
                          .map(player => (
                            <option key={player.id} value={player.id}>
                              {player.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Non-Striker *
                        {!nonStriker && (
                          <span className="ml-2 text-red-600 text-sm font-medium">
                            (Required)
                          </span>
                        )}
                      </label>
                      <select
                        value={nonStriker}
                        onChange={(e) => setNonStriker(e.target.value)}
                        disabled={loadingPlayers}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent disabled:bg-gray-100 ${
                          !nonStriker
                            ? 'border-red-300 bg-red-50'
                            : 'border-gray-300'
                        }`}
                      >
                        <option value="">
                          {loadingPlayers ? 'Loading players...' : 'Select non-striker'}
                        </option>
                        {(currentInnings === 1 ? team1Players : team2Players)
                          .filter(player => {
                            // Don't show striker in non-striker dropdown
                            if (player.id === striker) return false;
                            // Don't show dismissed batters
                            const battingEntry = playerStats.batting.find(b => b.player_id === player.id);
                            if (battingEntry?.is_out) return false;
                            return true;
                          })
                          .map(player => (
                            <option key={player.id} value={player.id}>
                              {player.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bowler *
                        {!bowler && (
                          <span className="ml-2 text-red-600 text-sm font-medium">
                            (Required)
                          </span>
                        )}
                        {currentBall === 0 && currentOver > 0 && !bowler && (
                          <span className="ml-2 text-orange-600 text-sm font-medium">
                            (New over - select new bowler)
                          </span>
                        )}
                      </label>
                      <select
                        value={bowler}
                        onChange={(e) => setBowler(e.target.value)}
                        disabled={loadingPlayers}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent disabled:bg-gray-100 ${
                          !bowler 
                            ? 'border-red-300 bg-red-50' 
                            : currentBall === 0 && currentOver > 0 && !bowler 
                            ? 'border-orange-300 bg-orange-50' 
                            : 'border-gray-300'
                        }`}
                      >
                        <option value="">
                          {loadingPlayers ? 'Loading players...' : 'Select bowler'}
                        </option>
                        {(currentInnings === 1 ? team2Players : team1Players).map(player => (
                          <option key={player.id} value={player.id}>
                            {player.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Ball Input */}
                  <div className="bg-white border-2 border-gray-200 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Ball Input</h4>
                    
                    {/* Cricket Rules Info */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                      <p className="text-sm text-blue-800">
                        <strong>Cricket Rules:</strong> Wide/No Ball base runs (1) don't change strike, but extra runs from running do. 
                        Bowler changes after each over. Striker rotates on odd runs (legal deliveries + extra runs from wide/no ball).
                      </p>
                    </div>
                    
                    {/* Ball Type Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Select Ball Type</label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {ballTypes.map(type => (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => handleBallTypeSelect(type.value)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                              selectedBallType === type.value
                                ? 'bg-cricket-green text-white'
                                : 'text-gray-700 bg-gray-100 hover:bg-cricket-green hover:text-white'
                            }`}
                          >
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Extra Runs Selection (for Wide) */}
                    {selectedBallType === 'wide' && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Additional Runs (batters ran while wide)
                        </label>
                        <div className="grid grid-cols-5 gap-2">
                          {[0, 1, 2, 3, 4].map(runs => (
                            <button
                              key={runs}
                              type="button"
                              onClick={() => handleExtraRunsSelect(runs)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                extraRuns === runs
                                  ? 'bg-blue-500 text-white'
                                  : 'text-gray-700 bg-gray-100 hover:bg-blue-500 hover:text-white'
                              }`}
                            >
                              {runs === 0 ? 'No Extra' : `+${runs}`}
                            </button>
                          ))}
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          Total runs: {1 + extraRuns} (Wide penalty: 1 + Extra runs: {extraRuns}) - All to extras
                        </div>
                      </div>
                    )}

                    {/* No Ball - Batter Runs + Extra Runs */}
                    {selectedBallType === 'no_ball' && (
                      <div className="mb-4 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Batter Runs (off the bat)
                          </label>
                          <div className="grid grid-cols-5 gap-2">
                            {[0, 1, 2, 3, 4, 6].map(runs => (
                              <button
                                key={runs}
                                type="button"
                                onClick={() => handleBatterRunsSelect(runs)}
                                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                  batterRuns === runs
                                    ? 'bg-green-500 text-white'
                                    : 'text-gray-700 bg-gray-100 hover:bg-green-500 hover:text-white'
                                }`}
                              >
                                {runs === 0 ? 'No Hit' : runs === 4 ? 'FOUR' : runs === 6 ? 'SIX' : runs}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Extra Runs (additional running)
                          </label>
                          <div className="grid grid-cols-5 gap-2">
                            {[0, 1, 2, 3, 4].map(runs => (
                              <button
                                key={runs}
                                type="button"
                                onClick={() => handleExtraRunsSelect(runs)}
                                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                  extraRuns === runs
                                    ? 'bg-blue-500 text-white'
                                    : 'text-gray-700 bg-gray-100 hover:bg-blue-500 hover:text-white'
                                }`}
                              >
                                {runs === 0 ? 'None' : `+${runs}`}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-700">
                            <strong>Total:</strong> {1 + batterRuns + extraRuns} runs
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            No Ball penalty (1) + Batter runs ({batterRuns}) go to extras.
                            Batter runs ({batterRuns}) credited to batter.
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Bye/Leg Bye Runs Selection */}
                    {selectedBallType && ['bye', 'leg_bye'].includes(selectedBallType) && (
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {selectedBallType === 'bye' ? 'Bye' : 'Leg Bye'} Runs
                        </label>
                        <div className="grid grid-cols-5 gap-2">
                          {[1, 2, 3, 4, 5].map(runs => (
                            <button
                              key={runs}
                              type="button"
                              onClick={() => handleExtraRunsSelect(runs)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                extraRuns === runs
                                  ? 'bg-blue-500 text-white'
                                  : 'text-gray-700 bg-gray-100 hover:bg-blue-500 hover:text-white'
                              }`}
                            >
                              {runs}
                            </button>
                          ))}
                        </div>
                        <div className="mt-2 text-sm text-gray-600">
                          {extraRuns} {selectedBallType === 'bye' ? 'bye' : 'leg bye'}(s) - All to extras (not to batter)
                        </div>
                      </div>
                    )}

                    {/* Confirm Button */}
                    {selectedBallType && (
                      <div className="flex justify-center">
                        <button
                          type="button"
                          onClick={confirmBallScoring}
                          className="px-6 py-2 bg-cricket-green text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
                        >
                          Score Ball
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Player Statistics */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-4">Current Player Statistics</h4>
                    
                    {/* Batting Stats */}
                    <div className="mb-4">
                      <h5 className="text-md font-medium text-gray-700 mb-2">Batting</h5>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-200">
                              <th className="px-2 py-1 text-left">Player</th>
                              <th className="px-2 py-1 text-center">Runs</th>
                              <th className="px-2 py-1 text-center">Balls</th>
                              <th className="px-2 py-1 text-center">4s</th>
                              <th className="px-2 py-1 text-center">6s</th>
                              <th className="px-2 py-1 text-center">SR</th>
                              <th className="px-2 py-1 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {playerStats.batting.map((batting, index) => (
                              <tr key={index} className={batting.player_id === striker ? 'bg-yellow-100' : ''}>
                                <td className="px-2 py-1">
                                  {getPlayerName(batting.player_id)}
                                  {batting.player_id === striker && ' (Striker)'}
                                </td>
                                <td className="px-2 py-1 text-center">{batting.runs}</td>
                                <td className="px-2 py-1 text-center">{batting.balls}</td>
                                <td className="px-2 py-1 text-center">{batting.fours}</td>
                                <td className="px-2 py-1 text-center">{batting.sixes}</td>
                                <td className="px-2 py-1 text-center">{batting.strike_rate}</td>
                                <td className="px-2 py-1 text-center">
                                  {batting.is_out ? 'Out' : 'Not Out'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Bowling Stats */}
                    <div>
                      <h5 className="text-md font-medium text-gray-700 mb-2">Bowling</h5>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-gray-200">
                              <th className="px-2 py-1 text-left">Player</th>
                              <th className="px-2 py-1 text-center">Overs</th>
                              <th className="px-2 py-1 text-center">Runs</th>
                              <th className="px-2 py-1 text-center">Wickets</th>
                              <th className="px-2 py-1 text-center">Economy</th>
                              <th className="px-2 py-1 text-center">Wides</th>
                              <th className="px-2 py-1 text-center">No Balls</th>
                            </tr>
                          </thead>
                          <tbody>
                            {playerStats.bowling.map((bowling, index) => (
                              <tr key={index} className={bowling.player_id === bowler ? 'bg-yellow-100' : ''}>
                                <td className="px-2 py-1">
                                  {getPlayerName(bowling.player_id)}
                                  {bowling.player_id === bowler && ' (Bowling)'}
                                </td>
                                <td className="px-2 py-1 text-center">{bowling.overs}</td>
                                <td className="px-2 py-1 text-center">{bowling.runs_conceded}</td>
                                <td className="px-2 py-1 text-center">{bowling.wickets}</td>
                                <td className="px-2 py-1 text-center">{bowling.economy_rate}</td>
                                <td className="px-2 py-1 text-center">{bowling.wide_balls}</td>
                                <td className="px-2 py-1 text-center">{bowling.no_balls}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Ball History */}
                  <div className="bg-gray-50 rounded-lg p-3 sm:p-4">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Ball History (Last 12 balls)</h4>
                    <div className="max-h-40 overflow-y-auto">
                      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 text-sm">
                        {ballHistory.slice(-12).map((ball, index) => (
                          <div
                            key={ball.id}
                            className={`p-2 rounded text-center ${
                              ball.isWicket ? 'bg-red-100 border border-red-300' :
                              ball.type === 'wide' || ball.type === 'no_ball' ? 'bg-yellow-100 border border-yellow-300' :
                              ball.type === '4' || ball.batterRuns === 4 ? 'bg-blue-100 border border-blue-300' :
                              ball.type === '6' || ball.batterRuns === 6 ? 'bg-purple-100 border border-purple-300' :
                              'bg-white border border-gray-200'
                            }`}
                          >
                            <div className="font-medium text-xs text-gray-500">{ball.over}.{ball.ball}</div>
                            <div className={`font-bold ${
                              ball.isWicket ? 'text-red-600' :
                              ball.type === 'wide' || ball.type === 'no_ball' ? 'text-yellow-700' :
                              ball.type === '4' || ball.batterRuns === 4 ? 'text-blue-600' :
                              ball.type === '6' || ball.batterRuns === 6 ? 'text-purple-600' :
                              'text-cricket-green'
                            }`}>
                              {ball.isWicket ? 'W' :
                               ball.type === 'wide' ? `Wd+${ball.extraRuns || 0}` :
                               ball.type === 'no_ball' ? `Nb+${(ball.batterRuns || 0) + (ball.extraRuns || 0)}` :
                               ball.type === 'bye' ? `B${ball.extraRuns || ball.runs}` :
                               ball.type === 'leg_bye' ? `Lb${ball.extraRuns || ball.runs}` :
                               ball.type === 'dot' ? '0' :
                               ball.runs || ball.type}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {ballHistory.length > 12 && (
                      <div className="text-xs text-gray-500 mt-2 text-center">
                        Showing last 12 of {ballHistory.length} balls
                      </div>
                    )}
                  </div>

                  {/* Man of the Match */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Man of the Match
                    </label>
                      <select
                        {...register('man_of_the_match')}
                        disabled={loadingPlayers}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent disabled:bg-gray-100"
                      >
                        <option value="">
                          {loadingPlayers ? 'Loading players...' : 'Select Man of the Match'}
                        </option>
                        {[...team1Players, ...team2Players].map(player => (
                          <option key={player.id} value={player.id}>
                            {player.name}
                          </option>
                        ))}
                      </select>
                  </div>

                  {/* Extras Breakdown Display */}
                  <div className="bg-yellow-50 rounded-lg p-3 sm:p-4">
                    <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Extras Breakdown</h4>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4 text-center">
                      <div>
                        <div className="text-sm text-gray-600">Wides</div>
                        <div className="text-lg font-bold text-yellow-700">{extras.wides}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">No Balls</div>
                        <div className="text-lg font-bold text-yellow-700">{extras.no_balls}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Byes</div>
                        <div className="text-lg font-bold text-yellow-700">{extras.byes}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Leg Byes</div>
                        <div className="text-lg font-bold text-yellow-700">{extras.leg_byes}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Total Extras</div>
                        <div className="text-lg font-bold text-yellow-800">{extras.total}</div>
                      </div>
                    </div>
                  </div>

                  {/* Partnership Display */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="text-lg font-semibold text-gray-800 mb-2">Current Partnership</h4>
                    <div className="flex justify-center space-x-8">
                      <div className="text-center">
                        <div className="text-sm text-gray-600">Runs</div>
                        <div className="text-2xl font-bold text-green-700">{partnership.runs}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-sm text-gray-600">Balls</div>
                        <div className="text-2xl font-bold text-green-700">{partnership.balls}</div>
                      </div>
                    </div>
                  </div>

                  {/* Fall of Wickets Display */}
                  {fallOfWickets.length > 0 && (
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="text-lg font-semibold text-gray-800 mb-2">Fall of Wickets</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-red-100">
                              <th className="px-2 py-1 text-left">#</th>
                              <th className="px-2 py-1 text-left">Score</th>
                              <th className="px-2 py-1 text-left">Overs</th>
                              <th className="px-2 py-1 text-left">Batter Out</th>
                              <th className="px-2 py-1 text-left">How Out</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fallOfWickets.map((fow, index) => (
                              <tr key={index}>
                                <td className="px-2 py-1">{fow.wicket_number}</td>
                                <td className="px-2 py-1">{fow.runs_at_fall}</td>
                                <td className="px-2 py-1">{fow.overs_at_fall}</td>
                                <td className="px-2 py-1">{getPlayerName(fow.batter_out)}</td>
                                <td className="px-2 py-1">{fow.how_out}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-between gap-2">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleUndoBall}
                        disabled={ballHistory.length === 0}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        Undo Last Ball
                      </button>
                      <button
                        type="button"
                        onClick={resetInnings}
                        className="flex-1 sm:flex-none px-3 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                      >
                        Reset Innings
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2 Actions */}
              <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4 sm:pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="order-2 sm:order-1 px-4 sm:px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  ← Back to Toss
                </button>
                <div className="order-1 sm:order-2 flex flex-col sm:flex-row gap-2 sm:gap-4">
                  <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 sm:px-6 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="px-4 sm:px-6 py-2 bg-cricket-green text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 text-sm"
                  >
                    {isLoading ? 'Completing...' : 'Complete Scorecard'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Dismissal Modal */}
      {showDismissalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[60]">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6 max-h-[95vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Wicket Details</h3>

            {/* Dismissal Type Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How Out? *
              </label>
              <select
                value={dismissalType}
                onChange={(e) => setDismissalType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              >
                <option value="">Select dismissal type</option>
                {dismissalTypes.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Which Batter Got Out (for run outs) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Who Got Out? *
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={() => setDismissedBatter('striker')}
                  className={`flex-1 px-3 sm:px-4 py-2 rounded-lg font-medium text-sm ${
                    dismissedBatter === 'striker'
                      ? 'bg-cricket-green text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Striker ({getPlayerName(striker)})
                </button>
                <button
                  type="button"
                  onClick={() => setDismissedBatter('non_striker')}
                  className={`flex-1 px-3 sm:px-4 py-2 rounded-lg font-medium text-sm ${
                    dismissedBatter === 'non_striker'
                      ? 'bg-cricket-green text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Non-Striker ({getPlayerName(nonStriker)})
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                For run outs, select which batter was dismissed
              </p>
            </div>

            {/* Fielder Selection (for caught, stumped, run out) */}
            {dismissalType && dismissalTypes.find(d => d.value === dismissalType)?.needsFielder && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fielder *
                </label>
                <select
                  value={fielder}
                  onChange={(e) => setFielder(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
                >
                  <option value="">Select fielder</option>
                  {(currentInnings === 1 ? team2Players : team1Players).map(player => (
                    <option key={player.id} value={player.id}>
                      {player.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Runs before wicket (for run outs while running) */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Runs Before Wicket (optional)
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[0, 1, 2, 3].map(runs => (
                  <button
                    key={runs}
                    type="button"
                    onClick={() => setBatterRuns(runs)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      batterRuns === runs
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-700 bg-gray-100 hover:bg-blue-500 hover:text-white'
                    }`}
                  >
                    {runs}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Runs completed before the wicket fell (e.g., run out while going for second run)
              </p>
            </div>

            {/* Dismissal Info Display */}
            {dismissalType && (
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-gray-700">
                  <strong>Dismissal:</strong> {getPlayerName(dismissedBatter === 'striker' ? striker : nonStriker)} - {dismissalTypes.find(d => d.value === dismissalType)?.label}
                  {fielder && ` by ${getPlayerName(fielder)}`}
                  {dismissalTypes.find(d => d.value === dismissalType)?.creditsBowler && ` (Bowler: ${getPlayerName(bowler)})`}
                </p>
              </div>
            )}

            {/* Modal Actions */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => {
                  setShowDismissalModal(false);
                  setSelectedBallType(null);
                  setDismissalType('');
                  setDismissedBatter('striker');
                  setFielder('');
                  setBatterRuns(0);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmWicket}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Confirm Wicket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Innings Complete Modal */}
      {showInningsCompleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">First Innings Complete!</h3>
              <p className="text-gray-600 mb-2">
                {inningsStatus.reason === 'all_out' && 'Team is All Out'}
                {inningsStatus.reason === 'overs_completed' && 'All Overs Completed'}
                {inningsStatus.reason === 'target_achieved' && 'Target Achieved'}
                {!inningsStatus.reason && 'Innings has ended'}
              </p>
              <p className="text-2xl font-bold text-cricket-green mb-4">
                Score: {team1Score.runs}/{team1Score.wickets} ({team1Score.overs} overs)
              </p>
              <p className="text-gray-500 mb-6">
                Target for 2nd Innings: <span className="font-semibold">{team1Score.runs + 1} runs</span>
              </p>
              <button
                onClick={() => {
                  setShowInningsCompleteModal(false);
                  switchInnings();
                  // Reset for new innings
                  setStriker('');
                  setNonStriker('');
                  setBowler('');
                  setInningsStatus({ completed: false, reason: null });
                  toast.success('Starting Second Innings!');
                }}
                className="w-full px-6 py-3 bg-cricket-green text-white rounded-lg hover:bg-green-600 font-semibold"
              >
                Start Second Innings
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScorecardForm;